import { Injectable } from '@angular/core';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import { OrganizationRepository } from '@ieCore/repositories/organization.repository';
import {
  CreateOrgDto,
  OrgResponseDto,
  UpdateEnumDto,
  UpdateOrgDto,
} from 'shared-general-libs/dto/org';
import { PaginatedResponseDto } from 'shared-general-libs/dto/pagination';
import { PaginationInterface } from '@ieShared/interfaces/pagination.interface';
import { EnumDetailsResponseDto } from 'shared-general-libs/dto/org';
import { Store } from '@ngrx/store';
import {
  selectUserOrganizationId,
  selectUserOrgContext,
} from '@ieApp/store/user/user.selectors';
import { Observable, filter, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';
import { UpdateOrgFeatureFlagsPayload } from '@ieShared/interfaces/organization.interface';

/**
 * Cast target for the derived-collection flags read below.
 *
 * C-2: these values normally arrive on the session payload (`GET /users/me`
 * → `orgConfig`, see `selectUserOrgContext`) and only fall back to the org
 * read shape below when the session payload doesn't carry `orgConfig` at
 * all (older backend during rollout).
 *
 * Cast note: these fields reach the sgl OrgResponseDto with the
 * feat/derived-cell-overlays sgl release; until sgl main carries them the org
 * payload simply omits them (=> false). Same precedent as
 * `multiOwnerCollectEnabled`. Do NOT strip the cast.
 */
type OrgConfigWithDerivedFlags = Partial<OrgResponseDto> & {
  wasteDerivedGenerationEnabled?: boolean;
  waterDerivedConsumptionEnabled?: boolean;
  fuelAndPowerEnergyEnabled?: boolean;
  fuelAndPowerGroupingEnabled?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  enumTranslationList: { [p: string]: string } = {};

  /** Resolve-once cache for the current tenant's BRSR-forms gate. */
  private brsrFormsEnabled$?: Observable<boolean>;

  /** Resolve-once cache for the current tenant's multi-data-owner gate. */
  private multiOwnerCollectEnabled$?: Observable<boolean>;

  /**
   * C-2: LIVE cache of the current tenant's config, keyed off the store's
   * orgId + session orgConfig (not resolved once-and-forever) and shared by
   * every per-org config reader below (activity form defaults — item 6,
   * idle logout — item 11, the derived-collection flags) so those features
   * cost at most ONE org HTTP call PER ORG, no matter how many of them are
   * used on a page — and normally cost NONE, since the session payload
   * (`GET /users/me` → `orgConfig`) already carries these values for every
   * role, whereas the org-read fallback needs `Organization.VIEW` and 403s
   * for data owners / reviewers.
   *
   * Deliberately NOT the same "resolve-once, take(1)" shape as
   * getBrsrFormsEnabled/getMultiOwnerCollectEnabled above: those gate a
   * single-tenant-lifetime UI concern, but activityFormDefaults/
   * idleLogoutMinutes must react to a logout + DIFFERENT-org login on the
   * SAME SPA instance (no full page reload) — a `take(1)` cache would keep
   * serving the FIRST org's config to the second org until reload. Instead
   * this tracks selectUserOrgContext's LIVE value (orgId + orgConfig read
   * atomically off ONE selector — see its doc comment for why two SEPARATE
   * store.select()s glitch against the real store): distinctUntilChanged +
   * switchMap re-resolves whenever either field changes, and an absent
   * orgId (logged out) resolves to `{}` immediately
   * with no HTTP call — both readers fail inert without a network round
   * trip. Fails OPEN to `{}` (not closed to a boolean) — each reader
   * derives its own safe/inert default from a missing field.
   */
  private orgConfig$?: Observable<Partial<OrgResponseDto>>;

  constructor(
    private orgRepo: OrganizationRepository,
    private store: Store,
  ) {}

  /**
   * Whether the CURRENT tenant has BRSR-as-Forms enabled (`Org.brsrFormsEnabled`).
   * Resolved at most once per app session: waits for the user store to expose the
   * orgId, fetches the org once, then replays the cached value to every consumer.
   *
   * FAILS CLOSED: any fetch error (e.g. 403 for users without Organization.VIEW)
   * resolves to `false`. Swallowing the error here is intentional and safe — this
   * value only gates the BRSR recognition-fill UI path, and the backend remains
   * the source of truth (a /brsr-fill save for a non-enabled tenant is rejected),
   * so a wrongly-closed gate degrades to the standard records path, never the
   * other way around.
   */
  getBrsrFormsEnabled(): Observable<boolean> {
    if (!this.brsrFormsEnabled$) {
      this.brsrFormsEnabled$ = this.store.select(selectUserOrganizationId).pipe(
        filter((orgId): orgId is string => !!orgId),
        take(1),
        switchMap((orgId) =>
          this.orgRepo.getCompany(orgId, { bypassForbiddenRedirect: true }),
        ),
        map((org) => org.brsrFormsEnabled === true),
        catchError(() => of(false)),
        shareReplay(1),
      );
    }
    return this.brsrFormsEnabled$;
  }

  /**
   * Whether the CURRENT tenant allows multiple data owners per facility
   * assignment (`Org.multiOwnerCollectEnabled`). Mirrors getBrsrFormsEnabled:
   * resolved at most once per app session and FAILS CLOSED — any fetch error
   * resolves to `false`, which degrades to the legacy single-owner chooser.
   *
   * The cast is temporary: `multiOwnerCollectEnabled` lands on the sgl
   * OrgResponseDto via the shared-general-libs feature/multi-data-owners
   * branch; until then the org payload simply omits the field (=> false).
   */
  getMultiOwnerCollectEnabled(): Observable<boolean> {
    if (!this.multiOwnerCollectEnabled$) {
      this.multiOwnerCollectEnabled$ = this.store
        .select(selectUserOrganizationId)
        .pipe(
          filter((orgId): orgId is string => !!orgId),
          take(1),
          switchMap((orgId) =>
            this.orgRepo.getCompany(orgId, { bypassForbiddenRedirect: true }),
          ),
          map(
            (org) =>
              (org as OrgResponseDto & { multiOwnerCollectEnabled?: boolean })
                .multiOwnerCollectEnabled === true,
          ),
          catchError(() => of(false)),
          shareReplay(1),
        );
    }
    return this.multiOwnerCollectEnabled$;
  }

  private getOrgConfig(): Observable<Partial<OrgResponseDto>> {
    if (!this.orgConfig$) {
      // R5: orgId + orgConfig read off ONE selector (selectUserOrgContext),
      // not combineLatest of two SEPARATE store.select()s — see that
      // selector's doc comment for why two separate selects glitch against
      // the real store (a stale-orgConfig tick paired with the fresh orgId,
      // firing a spurious org HTTP read on every login).
      this.orgConfig$ = this.store.select(selectUserOrgContext).pipe(
        distinctUntilChanged(
          (a, b) => a.orgId === b.orgId && a.orgConfig === b.orgConfig,
        ),
        switchMap(({ orgId, orgConfig }) => {
          if (!orgId) return of({}); // logged out / no org yet — inert
          // C-2: session-first. /users/me carries the tenant settings for EVERY role;
          // GET /orgs/:id needs Organization.VIEW and 403s (into {}) for data owners /
          // reviewers. Fall back to the org read only when the payload does not carry
          // orgConfig at all (older backend during rollout).
          if (orgConfig) return of(orgConfig as Partial<OrgResponseDto>);
          return this.orgRepo
            .getCompany(orgId, { bypassForbiddenRedirect: true })
            .pipe(catchError(() => of({})));
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.orgConfig$;
  }

  /**
   * Item 6: the CURRENT tenant's per-activity form-field defaults
   * (`Org.activityFormDefaults`), keyed by activity code then field key —
   * e.g. `{ MOBILE_COMBUSTION_ACTIVITY: { activityType: 'FUEL_USE' } }`.
   * Absent on the org record, or an org-fetch failure, resolves to `{}` —
   * FormService's merge no-ops on an empty map, so the served form config
   * passes through byte-identical.
   */
  getActivityFormDefaults(): Observable<
    Record<string, Record<string, string>>
  > {
    return this.getOrgConfig().pipe(
      map((org) => org.activityFormDefaults || {}),
    );
  }

  /**
   * Item 11: the CURRENT tenant's idle-logout timeout in minutes
   * (`Org.idleLogoutMinutes`). Absent, `0`, or an org-fetch failure all
   * resolve to `0`, which IdleTimeoutService treats as "feature off" —
   * inert, no timers started.
   */
  getIdleLogoutMinutes(): Observable<number> {
    return this.getOrgConfig().pipe(map((org) => org.idleLogoutMinutes || 0));
  }

  /**
   * Spec 1 (MRF waste package): whether the CURRENT tenant collects waste in
   * one grouped row with report-time derived "Waste generated"
   * (`Org.wasteDerivedGenerationEnabled`). Rides the LIVE org-config stream
   * (re-resolves on org switch, one HTTP call per org shared with the other
   * readers). Absent field or org-fetch failure => false — flag-off tenants
   * stay byte-identical. See `OrgConfigWithDerivedFlags` for the cast note.
   */
  getWasteDerivedGenerationEnabled(): Observable<boolean> {
    return this.getOrgConfig().pipe(
      map(
        (org) =>
          (org as OrgConfigWithDerivedFlags).wasteDerivedGenerationEnabled ===
          true,
      ),
    );
  }

  /**
   * Spec 2 plumbing (water package): exposed here so the flag infrastructure
   * ships once, but consumed by NOTHING yet — the report overlay for it
   * throws NotImplemented, and no admin UI surfaces it.
   */
  getWaterDerivedConsumptionEnabled(): Observable<boolean> {
    return this.getOrgConfig().pipe(
      map(
        (org) =>
          (org as OrgConfigWithDerivedFlags).waterDerivedConsumptionEnabled ===
          true,
      ),
    );
  }

  /**
   * Spec C §4.3: whether this tenant derives BRSR energy (GJ) from its Scope-1/2
   * entries. Read by nothing in the FE render path (the report page reads the report's
   * STAMP, not this) but exposed so the admin switch and any future preview share one
   * source of truth. Absent field or org-fetch failure => false.
   */
  getFuelAndPowerEnergyEnabled(): Observable<boolean> {
    return this.getOrgConfig().pipe(
      map(
        (org) =>
          (org as OrgConfigWithDerivedFlags).fuelAndPowerEnergyEnabled === true,
      ),
    );
  }

  /**
   * Spec C §4.6: whether Scope 1 + Scope 2 collapse into one "Fuel & Power" row on
   * the task and review screens. Rides the same LIVE org-config stream as the waste
   * flag (one HTTP call per org, re-resolved on org switch).
   */
  getFuelAndPowerGroupingEnabled(): Observable<boolean> {
    return this.getOrgConfig().pipe(
      map(
        (org) =>
          (org as OrgConfigWithDerivedFlags).fuelAndPowerGroupingEnabled ===
          true,
      ),
    );
  }

  getCompanies(
    params: PaginationInterface,
  ): RequestLoadingType<PaginatedResponseDto<OrgResponseDto>> {
    return requestLoading(this.orgRepo.getCompanies(params));
  }

  getCompany(companyId: string): RequestLoadingType<OrgResponseDto> {
    return requestLoading(this.orgRepo.getCompany(companyId));
  }

  getEnums(companyId: string): RequestLoadingType<EnumDetailsResponseDto[]> {
    return requestLoading(this.orgRepo.getEnums(companyId));
  }

  getEnumTranslationList(): RequestLoadingType<{ [p: string]: string }> {
    return requestLoading(this.orgRepo.getEnumTranslationList());
  }

  getEnumList(key: string): RequestLoadingType<{ [p: string]: string }> {
    return requestLoading(this.orgRepo.getEnumList(key));
  }

  updateEnums(
    id: string,
    enumId: string,
    keyPair: UpdateEnumDto,
  ): RequestLoadingType<EnumDetailsResponseDto> {
    return requestLoading(this.orgRepo.updateEnums(id, enumId, keyPair));
  }

  createCompany(data: CreateOrgDto): RequestLoadingType<OrgResponseDto> {
    return requestLoading(this.orgRepo.createCompany(data));
  }

  updateCompany(
    companyId: string,
    data: UpdateOrgDto,
  ): RequestLoadingType<OrgResponseDto> {
    return requestLoading(this.orgRepo.updateCompany(companyId, data));
  }

  deleteCompany(companyId: string): RequestLoadingType<void> {
    return requestLoading(this.orgRepo.deleteCompany(companyId));
  }

  updateOrgFeatureFlags(
    orgId: string,
    flags: UpdateOrgFeatureFlagsPayload,
  ): RequestLoadingType<OrgResponseDto> {
    return requestLoading(this.orgRepo.updateOrgFeatureFlags(orgId, flags));
  }
}
